import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { companyService, personService } from "../services/svc_entities";
import { queryKeys } from "../lib/queryKeys";
import { ListRequest } from "../types/common/commonTypes";
import {
  CompanyType,
  CompanyCreateGroup,
  CompanyUpdateGroup,
} from "../types/companyTypes";
import {
  PersonUpdateGroup,
  PersonCreateGroup,
  PersonType,
} from "../types/personTypes";

export const useTSQCompanyList = (
  params: ListRequest,
  company_type: CompanyType,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.company.list(params, company_type),
    queryFn: () => companyService.list(params, company_type),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQCompany = (
  id: string | null,
  company_type: CompanyType,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.company.detail(id!, company_type),
    queryFn: () => {
      return companyService.getById(id!, company_type);
    },
    enabled: !!id && enabled,
  });
};

export const useTSMCreateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CompanyCreateGroup) => {
      return companyService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.company.lists() });
    },
    onError: (error) => {
      console.error("Failed to create Company:", error);
    },
  });
};

export const useTSMUpdateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CompanyUpdateGroup }) => {
      return companyService.update(id, data);
    },
    onSuccess: (updatedCompany) => {
      queryClient.setQueryData(
        queryKeys.company.detail(
          updatedCompany.id,
          updatedCompany.company_type,
        ),
        updatedCompany,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.company.lists() });
    },
    onError: (error) => {
      console.error("Failed to update Company:", error);
    },
  });
};

export const useTSMDeactivateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      company_type,
    }: {
      id: string;
      company_type: CompanyType;
    }) => {
      return companyService.deactivate(id, company_type);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.company.lists() });
    },
    onError: (error) => {
      console.error("Failed to deactivate Company:", error);
    },
  });
};

export const useTSMReactivateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      company_type,
    }: {
      id: string;
      company_type: CompanyType;
    }) => {
      return companyService.reactivate(id, company_type);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.company.lists() });
    },
    onError: (error) => {
      console.error("Failed to reactivate Company:", error);
    },
  });
};

export const useTSQPeopleList = (
  params: ListRequest,
  person_type: PersonType,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.person.list(params, person_type),
    queryFn: () => personService.list(params, person_type),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQPerson = (id: string, person_type: PersonType) => {
  return useQuery({
    queryKey: queryKeys.person.detail(id, person_type),
    queryFn: () => {
      return personService.getById(id, person_type);
    },
    enabled: !!id,
  });
};

export const useTSMCreatePerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PersonCreateGroup) => {
      return personService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.person.lists() });
    },
    onError: (error) => {
      console.error("Failed to create Person:", error);
    },
  });
};

export const useTSMUpdatePerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PersonUpdateGroup }) => {
      return personService.update(id, data);
    },
    onSuccess: (updatedPerson) => {
      queryClient.setQueryData(
        queryKeys.person.detail(updatedPerson.id, updatedPerson.person_type),
        updatedPerson,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.person.lists() });
    },
    onError: (error) => {
      console.error("Failed to update Company:", error);
    },
  });
};

export const useTSMDeactivatePerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      person_type,
    }: {
      id: string;
      person_type: PersonType;
    }) => {
      return personService.deactivate(id, person_type);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.person.lists() });
    },
    onError: (error) => {
      console.error("Failed to deactivate Person:", error);
    },
  });
};

export const useTSMReactivatePerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      person_type,
    }: {
      id: string;
      person_type: PersonType;
    }) => {
      return personService.reactivate(id, person_type);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.person.lists() });
    },
    onError: (error) => {
      console.error("Failed to reactivate Person:", error);
    },
  });
};
